import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { AddUsersToModulesAsManagersSuccess, RefreshModuleUsersOnlineState, RemoveUsersFromModulesAsManagersSuccess } from 'app/library/module-user/actions';
import { AddModuleUsers } from 'app/library/module-user/actions/add-entities.action';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleUserState } from 'app/library/module-user/models/module-user.state';
import { ModuleState } from 'app/library/module/models/module.state';
import { UserService } from 'app/library/user/services/user.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { map } from 'rxjs/operators';
import {
    DeselectSelectedModuleManagers,
    FetchModuleManagers,
    FetchModuleManagersFailure,
    FetchModuleManagersSuccess,
    SearchModuleManagers,
    SelectFilteredModuleManagers,
    SetModuleManagers,
    ToggleSelectedModuleManager,
    UnsetModuleManagers,
} from '../actions';
import { ModuleManagersApiService } from '../services/module-managers.api-service';
import { ModuleManagersStateModel } from './module-managers.state-model';

@State<ModuleManagersStateModel>({
    name: 'managers',
    defaults: new ModuleManagersStateModel(),
})
@Injectable()
export class ModuleManagersState extends SubentityState<ModuleUser> {
    @Selector([ModuleUserState.getEntities])
    static getEntities(state: ModuleManagersStateModel, moduleUsers: ModuleUser[]): ModuleUser[] {
        if (!moduleUsers) {
            return null;
        }

        return moduleUsers.filter((moduleUser) => state.list.indexOf(moduleUser.id) !== -1);
    }

    @Selector([ModuleUserState.getEntities, UserState.getEntities])
    static getEntitiesWithUser(state: ModuleManagersStateModel, moduleUsers: ModuleUser[], users: User[]): ModuleUserExtended[] {
        const toReturn = ModuleManagersState.getEntities(state, moduleUsers);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<ModuleUserExtended>((moduleUser) =>
                Object.assign<ModuleUserExtended, ModuleUser, Partial<ModuleUserExtended>>(new ModuleUserExtended(), moduleUser, {
                    user: usersContainer[moduleUser.userId] || null,
                })
            );
        }

        return null;
    }

    @Selector([ModuleUserState.getEntities])
    static getFilteredEntities(state: ModuleManagersStateModel, moduleUsers: ModuleUser[]): ModuleUser[] {
        if (!moduleUsers) {
            return null;
        }

        return moduleUsers.filter((moduleUser) => state.filtered.indexOf(moduleUser.id) !== -1);
    }

    @Selector([ModuleUserState.getEntities, UserState.getEntities])
    static getFilteredEntitiesWithUser(state: ModuleManagersStateModel, moduleUsers: ModuleUser[], users: User[]): ModuleUserExtended[] {
        const toReturn = ModuleManagersState.getFilteredEntities(state, moduleUsers);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<ModuleUserExtended>((moduleUser) =>
                Object.assign<ModuleUserExtended, ModuleUser, Partial<ModuleUserExtended>>(new ModuleUserExtended(), moduleUser, {
                    user: usersContainer[moduleUser.userId] || null,
                })
            );
        }

        return null;
    }

    @Selector()
    static getSelectedEntityIds(state: ModuleManagersStateModel): string[] {
        return state.selected;
    }

    constructor(private moduleManagersService: ModuleManagersApiService, private _userService: UserService, private store: Store, private _featureService: FeatureService) {
        super(null, 30);

        this._propstoSearch = ['user', ...User.props_to_search];
    }

    @Action(SetModuleManagers)
    setAll(ctx: StateContext<ModuleManagersStateModel>, action: SetModuleManagers): void {
        this._setSubentities(ctx, action, AddModuleUsers);

        setTimeout(() => {
            const moduleManagersWithUser = this.store.selectSnapshot(ModuleManagersState.getEntitiesWithUser);

            if (moduleManagersWithUser && this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                this.store.dispatch(new RefreshModuleUsersOnlineState(moduleManagersWithUser));
                this._userService.setOnlineStateInterval(RefreshModuleUsersOnlineState, ModuleManagersState.getEntitiesWithUser);
            }
        });
    }

    @Action(UnsetModuleManagers)
    unsetAll(ctx: StateContext<ModuleManagersStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchModuleManagers)
    fetchAll(ctx: StateContext<ModuleManagersStateModel>, action: FetchModuleManagers): void {
        this._fetchSubentities(
            ctx,
            action,
            SetModuleManagers,
            FetchModuleManagersSuccess,
            FetchModuleManagersFailure,
            this.moduleManagersService.getAll(action.payload).pipe(
                map((res) => {
                    ctx.patchState({
                        totalCount: res.pagination.number_of_results,
                    });

                    return res.data.map((query) => new ModuleUserExtended(query));
                })
            )
        );
    }

    @Action(FetchModuleManagersSuccess)
    fetchAllSuccess(ctx: StateContext<ModuleManagersStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchModuleManagersFailure)
    fetchAllFailure(ctx: StateContext<ModuleManagersStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedModuleManager)
    toggleSelect(ctx: StateContext<ModuleManagersStateModel>, action: ToggleSelectedModuleManager): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModuleManagers)
    selectAll(ctx: StateContext<ModuleManagersStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModuleManagers)
    deselectAll(ctx: StateContext<ModuleManagersStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchModuleManagers)
    search(ctx: StateContext<ModuleManagersStateModel>, action: SearchModuleManagers): void {
        const users = this.store.selectSnapshot(UserState.getEntities);
        const moduleUsers = this.store.selectSnapshot(ModuleUserState.getEntities);
        const extendedModuleUsers = moduleUsers.map((moduleUser) => ({
            ...moduleUser,
            user: users.filter((user) => user.id === moduleUser.userId)[0],
            module: null,
        }));

        this._searchSubentities(ctx, action, extendedModuleUsers);
    }

    @Action(AddUsersToModulesAsManagersSuccess)
    addUsersToModulesAsManagersSuccess(ctx: StateContext<ModuleManagersStateModel>, action: AddUsersToModulesAsManagersSuccess): void {
        const moduleUsers = this.store.selectSnapshot(ModuleUserState.getEntities);
        const currentModule = this.store.selectSnapshot(ModuleState.getEntity);

        if (currentModule) {
            this._setSubentities(ctx, { payload: moduleUsers.filter((moduleUser) => moduleUser.isManager && moduleUser.moduleId === currentModule.id) }, AddModuleUsers);
        }
    }

    @Action(RemoveUsersFromModulesAsManagersSuccess)
    removeUsersFromModulesAsManagersSuccess(ctx: StateContext<ModuleManagersStateModel>, action: RemoveUsersFromModulesAsManagersSuccess): void {
        const moduleUsers = this.store.selectSnapshot(ModuleUserState.getEntities);
        const currentModule = this.store.selectSnapshot(ModuleState.getEntity);

        if (currentModule) {
            this._setSubentities(ctx, { payload: moduleUsers.filter((moduleUser) => moduleUser.isManager && moduleUser.moduleId === currentModule.id) }, AddModuleUsers);
        }
    }
}
